"use client"

import { useCallback, useRef, useState } from "react"
import { Save, ChevronDown, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { SavedEconomicsPreset, AnalysisItem } from "./types"
import { useTranslations } from "next-intl"


interface PresetSaveDropdownProps {
  activePreset: SavedEconomicsPreset | null
  isDirty: boolean
  items: AnalysisItem[]
  onSave: (name: string, items: AnalysisItem[]) => Promise<SavedEconomicsPreset>
  onUpdate: (id: string, updates: { name?: string; items?: AnalysisItem[] }) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onPresetSaved: (preset: SavedEconomicsPreset) => void
  onPresetDeleted: () => void
}

export function PresetSaveDropdown({
  activePreset,
  isDirty,
  items,
  onSave,
  onUpdate,
  onDelete,
  onPresetSaved,
  onPresetDeleted,
}: PresetSaveDropdownProps) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsPresetSaveDropdown")
  const tCommonUi = useTranslations("Common")

  const [showNameDialog, setShowNameDialog] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [presetName, setPresetName] = useState("")
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasItems = items.length > 0

  const handleSaveNew = useCallback(() => {
    setPresetName("")
    setShowNameDialog(true)
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  const handleConfirmSave = useCallback(async () => {
    const name = presetName.trim()
    if (!name || saving) return
    setSaving(true)
    try {
      const preset = await onSave(name, items)
      onPresetSaved(preset)
      setShowNameDialog(false)
    } catch (_err) {
      // Error is surfaced via hook's error state
    } finally {
      setSaving(false)
    }
  }, [presetName, saving, items, onSave, onPresetSaved])

  const handleUpdateCurrent = useCallback(async () => {
    if (!activePreset || saving) return
    setSaving(true)
    try {
      await onUpdate(activePreset.id, { items })
      onPresetSaved({ ...activePreset, items, updated_at: new Date().toISOString() })
    } catch {
      // Error is surfaced via hook's error state
    } finally {
      setSaving(false)
    }
  }, [activePreset, saving, items, onUpdate, onPresetSaved])

  const handleDeleteConfirm = useCallback(async () => {
    if (!activePreset || saving) return
    setSaving(true)
    try {
      await onDelete(activePreset.id)
      onPresetDeleted()
      setShowDeleteConfirm(false)
    } catch {
      // Error is surfaced via hook's error state
    } finally {
      setSaving(false)
    }
  }, [activePreset, saving, onDelete, onPresetDeleted])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={!hasItems}>
            <Save className="h-3.5 w-3.5 mr-1.5" />
            <span><span key={tUi("savePreset")}>{tUi("savePreset")}</span></span>
            {isDirty && activePreset && (
              <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-amber-500" />
            )}
            <ChevronDown className="h-3.5 w-3.5 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* setTimeout lets the dropdown fully close before the dialog opens,
              preventing a Radix pointer-events race that freezes the page. */}
          <DropdownMenuItem onSelect={() => setTimeout(handleSaveNew, 0)}>
            <span key={tUi("saveAsNew")}>{tUi("saveAsNew")}</span></DropdownMenuItem>
          {activePreset && (
            <DropdownMenuItem onSelect={handleUpdateCurrent} disabled={!isDirty}>
              <span><span key={tUi("update")}>{tUi("update")}</span></span>{activePreset.name}<span>&rdquo;</span>
            </DropdownMenuItem>
          )}
          {activePreset && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setTimeout(() => setShowDeleteConfirm(true), 0)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                <span><span key={tUi("delete2")}>{tUi("delete2")}</span></span>{activePreset.name}<span>&rdquo;</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save-as-new dialog */}
      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle><span key={tUi("savePreset")}>{tUi("savePreset")}</span></DialogTitle>
            <DialogDescription><span key={tUi("nameThisAnalysisConfigurationForQuickAccessLater")}>{tUi("nameThisAnalysisConfigurationForQuickAccessLater")}</span></DialogDescription>
          </DialogHeader>
          <Input
            ref={inputRef}
            placeholder={tUi("eGQ4Bestsellers")}
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleConfirmSave()
            }}
            maxLength={60}
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowNameDialog(false)}>
              <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button size="sm" onClick={handleConfirmSave} disabled={!presetName.trim() || saving}>
              <span key={saving ? tUi("saving") : tUi("save")}>{saving ? tUi("saving") : tUi("save")}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle><span key={tUi("deletePreset")}>{tUi("deletePreset")}</span></DialogTitle>
            <DialogDescription>
              <span><span key={tUi("delete2")}>{tUi("delete2")}</span></span>{activePreset?.name}<span><span key={tUi("thisCannotBeUndone")}>{tUi("thisCannotBeUndone")}</span></span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>
              <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteConfirm} disabled={saving}>
              <span key={saving ? tUi("deleting") : tUi("delete")}>{saving ? tUi("deleting") : tUi("delete")}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
